'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, FileText, Type, Hash, List } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomLoremIpsumContent } from '../../data/random-lorem-ipsum-generator-content';
import Link from 'next/link';

type LoremType = 'paragraphs' | 'sentences' | 'words';

const loremIpsum = [
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
    'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.',
    'Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore.',
    'Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia.',
    'Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit.',
    'Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet.',
    'Consectetur, adipisci velit, sed quia non numquam eius modi tempora incidunt.'
];

const loremWords = [
    'lorem', 'ipsum', 'dolor', 'sit', 'amet', 'consectetur', 'adipiscing', 'elit',
    'sed', 'do', 'eiusmod', 'tempor', 'incididunt', 'ut', 'labore', 'et', 'dolore',
    'magna', 'aliqua', 'enim', 'ad', 'minim', 'veniam', 'quis', 'nostrud',
    'exercitation', 'ullamco', 'laboris', 'nisi', 'aliquip', 'ex', 'ea', 'commodo',
    'consequat', 'duis', 'aute', 'irure', 'in', 'reprehenderit', 'voluptate',
    'velit', 'esse', 'cillum', 'fugiat', 'nulla', 'pariatur', 'excepteur', 'sint',
    'occaecat', 'cupidatat', 'non', 'proident', 'sunt', 'culpa', 'qui', 'officia',
    'deserunt', 'mollit', 'anim', 'id', 'est', 'laborum'
];

export default function RandomLoremIpsumGenerator() {
    const [output, setOutput] = useState('');
    const [loremType, setLoremType] = useState<LoremType>('paragraphs');
    const [count, setCount] = useState(3);
    const [copied, setCopied] = useState(false);
    const [startWithLorem, setStartWithLorem] = useState(true);
    const { toast } = useToast();

    const getRandomInt = (min: number, max: number) => {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    const getRandomSentence = useCallback(() => {
        const sentenceLength = getRandomInt(5, 15);
        let sentence = '';

        for (let i = 0; i < sentenceLength; i++) {
            const word = loremWords[Math.floor(Math.random() * loremWords.length)];
            sentence += (i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word) + ' ';
        }

        return sentence.trim() + '.';
    }, []);

    const getRandomParagraph = useCallback(() => {
        const sentenceCount = getRandomInt(3, 8);
        let paragraph = '';

        for (let i = 0; i < sentenceCount; i++) {
            paragraph += getRandomSentence() + ' ';
        }

        return paragraph.trim();
    }, [getRandomSentence]);

    const generateLoremIpsum = useCallback(() => {
        let result: string[] = [];

        switch (loremType) {
            case 'paragraphs':
                for (let i = 0; i < count; i++) {
                    result.push(startWithLorem && i === 0 ? loremIpsum[0] : getRandomParagraph());
                }
                return result.join('\n\n');

            case 'sentences':
                for (let i = 0; i < count; i++) {
                    result.push(startWithLorem && i === 0 ? loremIpsum[0] : getRandomSentence());
                }
                return result.join(' ');

            case 'words':
                let words: string[] = [];
                if (startWithLorem) {
                    words = ['Lorem', 'ipsum', ...loremIpsum[0].split(' ').slice(2)];
                    if (words.length > count) {
                        words = words.slice(0, count);
                    } else {
                        while (words.length < count) {
                            words.push(loremWords[Math.floor(Math.random() * loremWords.length)]);
                        }
                    }
                } else {
                    for (let i = 0; i < count; i++) {
                        words.push(loremWords[Math.floor(Math.random() * loremWords.length)]);
                    }
                }
                return words.join(' ');

            default:
                return '';
        }
    }, [loremType, count, startWithLorem, getRandomParagraph, getRandomSentence]);

    const generateNewOutput = useCallback(() => {
        setOutput(generateLoremIpsum());
        setCopied(false);
    }, [generateLoremIpsum]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    }, [toast]);

    useEffect(() => {
        generateNewOutput();
    }, [generateNewOutput]);

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Random Lorem Ipsum Generator">
                Generate random Lorem Ipsum text with our free online tool. Create placeholder text for web design, typography testing, and content development.
            </ToolPageIntro>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Generate Lorem Ipsum</CardTitle>
                            <CardDescription>Create placeholder text for your designs and mockups</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <div className="relative">
                                    <textarea
                                        value={output}
                                        readOnly
                                        className="w-full min-h-[200px] p-4 pr-16 border rounded-md font-mono text-sm bg-muted/50"
                                        placeholder="Your lorem ipsum text will appear here..."
                                        style={{ paddingRight: '4.5rem' }}
                                    />
                                    <div className="absolute right-3 top-3 flex flex-col space-y-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={() => copyToClipboard(output)}
                                            disabled={!output}
                                            className="h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-background"
                                            aria-label="Copy to clipboard"
                                        >
                                            {copied ? (
                                                <Check className="h-4 w-4" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="icon"
                                            onClick={generateNewOutput}
                                            className="h-8 w-8 bg-background/80 backdrop-blur-sm hover:bg-background"
                                            aria-label="Generate new text"
                                        >
                                            <RefreshCw className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Text Type</Label>
                                    <Select value={loremType} onValueChange={(value) => setLoremType(value as LoremType)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select text type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="paragraphs">
                                                <div className="flex items-center">
                                                    <Type className="h-4 w-4 mr-2" />
                                                    Paragraphs
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="sentences">
                                                <div className="flex items-center">
                                                    <List className="h-4 w-4 mr-2" />
                                                    Sentences
                                                </div>
                                            </SelectItem>
                                            <SelectItem value="words">
                                                <div className="flex items-center">
                                                    <Hash className="h-4 w-4 mr-2" />
                                                    Words
                                                </div>
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="count">
                                        {loremType === 'paragraphs' ? 'Number of paragraphs' :
                                            loremType === 'sentences' ? 'Number of sentences' : 'Number of words'}
                                    </Label>
                                    <div className="flex items-center space-x-4">
                                        <Input
                                            id="count"
                                            type="number"
                                            min="1"
                                            max={loremType === 'words' ? '1000' : '100'}
                                            value={count}
                                            onChange={(e) => {
                                                const max = loremType === 'words' ? 1000 : 100;
                                                const value = Math.min(Math.max(1, parseInt(e.target.value) || 1), max);
                                                setCount(value);
                                            }}
                                            className="w-24"
                                        />
                                        <div className="flex-1">
                                            <Slider
                                                value={[count]}
                                                onValueChange={([value]) => {
                                                    const max = loremType === 'words' ? 1000 : 100;
                                                    setCount(Math.min(Math.max(1, value), max));
                                                }}
                                                min={1}
                                                max={loremType === 'words' ? 1000 : 100}
                                                step={1}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="start-with-lorem"
                                    checked={startWithLorem}
                                    onChange={(e) => setStartWithLorem(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <Label htmlFor="start-with-lorem">Start with "Lorem ipsum dolor sit amet..."</Label>
                            </div>

                            <div className="pt-2">
                                <Button
                                    onClick={generateNewOutput}
                                    className="w-full md:w-auto"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate New Text
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>About Lorem Ipsum</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Lorem ipsum is a placeholder text commonly used to demonstrate the visual form of a document or a typeface without relying on meaningful content.
                            </p>

                            <div className="space-y-2">
                                <h3 className="font-semibold flex items-center">
                                    <FileText className="h-4 w-4 mr-2" />
                                    Common Uses
                                </h3>
                                <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground">
                                    <li>Web design mockups</li>
                                    <li>Print layouts</li>
                                    <li>Typography demonstrations</li>
                                    <li>Filling text in designs</li>
                                    <li>Testing layouts</li>
                                </ul>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-semibold flex items-center">
                                    <Type className="h-4 w-4 mr-2" />
                                    Text Types
                                </h3>
                                <div className="space-y-2">
                                    <div className="bg-muted p-3 rounded">
                                        <h4 className="font-medium">Paragraphs</h4>
                                        <p className="text-xs text-muted-foreground mt-1">Generate multiple paragraphs of text with proper spacing.</p>
                                    </div>
                                    <div className="bg-muted p-3 rounded">
                                        <h4 className="font-medium">Sentences</h4>
                                        <p className="text-xs text-muted-foreground mt-1">Get a specific number of sentences in a single block.</p>
                                    </div>
                                    <div className="bg-muted p-3 rounded">
                                        <h4 className="font-medium">Words</h4>
                                        <p className="text-xs text-muted-foreground mt-1">Generate a specific number of words, up to 1,000 at once.</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomLoremIpsumContent.title}
                description={randomLoremIpsumContent.description}
                content={randomLoremIpsumContent.content}
                faqs={randomLoremIpsumContent.faqs}
                relatedTools={randomLoremIpsumContent.relatedTools}
            />
        </div>
    );
}